import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import ProjectMapHelper, {
  SubProjectGroup
} from 'src/app/helpers/project-map-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import Employee from 'src/app/model/product/employee';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import LastSelectedPlanValues from '../../helper-interfaces/last-selected-plan-values';
import GroupSelectedEvent from '../../helper-interfaces/group-selected-event';
import { AdminPlanningMarkerProjectInfoComponent } from '../admin-planning-marker-project-info/admin-planning-marker-project-info.component';
import { SubProject } from 'src/app/model/project/sub-project';
import SubProjectPlannedEvent from '../../helper-interfaces/subproject-planned-event';

@Component({
  selector: 'admin-planning-marker-popup',
  templateUrl: './admin-planning-marker-popup.component.html',
  styleUrls: ['./admin-planning-marker-popup.component.scss']
})
export class AdminPlanningMarkerPopupComponent
  extends SubscriptionBasedComponent
  implements OnInit, OnDestroy, OnChanges {
  @Input()
  subProjectGroup: SubProjectGroup;
  @Input()
  placerOptions: Employee[];
  @Input()
  showSubmitPlanContent: boolean = true;
  @Input()
  hidePlannedProjects: boolean;
  @Input()
  groupSelected: EventEmitter<GroupSelectedEvent>;

  @Output()
  updatePlanValues = new EventEmitter<LastSelectedPlanValues>();
  @Output()
  closeInfoWindow = new EventEmitter<any>();

  toggleDisplay = new EventEmitter<number>();

  @ViewChildren(AdminPlanningMarkerProjectInfoComponent)
  children: QueryList<AdminPlanningMarkerProjectInfoComponent>;

  init: boolean = false;
  displayListClass: string = AdminPlanningMarkerPopupComponent.SHOW_LIST_CLASS;
  alwaysDisplay: boolean = false;

  private static readonly SHOW_LIST_CLASS: string = '';
  private static readonly HIDE_LIST_CLASS: string = 'hidden';

  ngOnInit(): void {
    this.calculateAlwaysDisplay(this.hidePlannedProjects);
    this.calculateDisplayListClass(this.hidePlannedProjects);
    this.preprocessSubProjects();
    if (this.groupSelected != null) {
      this.addSubscription(this.groupSelected, (event: GroupSelectedEvent) => {
        if (
          event.lastSelectedPlanValues != null &&
          event.group === this.subProjectGroup
        ) {
          this.children.forEach(c =>
            c.fillPlanValues(event.lastSelectedPlanValues)
          );
        }
      });
    }
    this.init = true;
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.toggleDisplay.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hidePlannedProjects) {
      this.calculateAlwaysDisplay(changes.hidePlannedProjects.currentValue);
      this.calculateDisplayListClass(changes.hidePlannedProjects.currentValue);
    }
  }

  displayChild(index: number): void {
    this.displayListClass = AdminPlanningMarkerPopupComponent.HIDE_LIST_CLASS;
    this.toggleDisplay.next(index);
  }

  backPressed(): void {
    this.displayListClass = AdminPlanningMarkerPopupComponent.SHOW_LIST_CLASS;
  }

  private calculateDisplayListClass(hidePlannedProjects: boolean): void {
    if (!hidePlannedProjects) {
      this.displayListClass =
        this.subProjectGroup.subProjects.length > 1
          ? AdminPlanningMarkerPopupComponent.SHOW_LIST_CLASS
          : AdminPlanningMarkerPopupComponent.HIDE_LIST_CLASS;
    } else {
      this.displayListClass =
        CollectionHelper.count(
          this.subProjectGroup.subProjects,
          sp => !sp.planned
        ) === 1
          ? AdminPlanningMarkerPopupComponent.HIDE_LIST_CLASS
          : AdminPlanningMarkerPopupComponent.SHOW_LIST_CLASS;
    }
  }

  private calculateAlwaysDisplay(hidePlannedProjects: boolean): void {
    this.alwaysDisplay =
      this.subProjectGroup.subProjects.length === 1 ||
      (hidePlannedProjects &&
        CollectionHelper.count(
          this.subProjectGroup.subProjects,
          sp => sp.planned
        ) === 1);
  }

  private preprocessSubProjects(): void {
    this.subProjectGroup.subProjects.forEach(sp => {
      this.calculatePopupDisplay(sp);
    });
  }

  private calculatePopupDisplay(sp: SubProject) {
    const displayProductList =
      sp.project != null && CollectionHelper.isPopulated(sp.project.products)
        ? StringHelper.join(
            sp.project.products.map(prod =>
              StringHelper.shortenText(ProductHelper.getTypeOf(prod), 30)
            ),
            ', '
          )
        : '';
    const displayPlacementDate = ProjectHelper.getCurrentConfirmedPlacementDate(
      sp
    );
    sp.popupDisplay = StringHelper.join(
      [
        sp.project == null ? null : `#${sp.project.id.toString()}`,
        displayProductList,
        displayPlacementDate
      ],
      ' - '
    );
  }

  subProjectPlanned(event: SubProjectPlannedEvent): void {
    const index = this.subProjectGroup.subProjects.findIndex(
      sp => sp.id === event.subProject.id
    );
    if (index >= 0) {
      this.subProjectGroup.subProjects[index] = event.subProject;
    }
    event.subProject.planned = true;
    if (
      this.hidePlannedProjects &&
      this.subProjectGroup.subProjects.every(sp => sp.planned)
    ) {
      this.closeInfoWindow.emit();
    }
    this.calculatePopupDisplay(event.subProject);
    this.updatePlanValues.emit(event.lastSelectedPlanValues);
    this.subProjectGroup.markerColor = ProjectMapHelper.calculateGroupMarkerColor(
      this.subProjectGroup
    );
    this.children.forEach(c => c.fillPlanValues(event.lastSelectedPlanValues));
  }

  trackById(index: number, obj: any): number {
    return GeneralHelper.trackById(index, obj);
  }
}
